/**
 * The single place the app chooses an auth implementation: real Cognito when
 * the pool is configured, the local fake otherwise.
 */

import { inject, type App, type InjectionKey } from 'vue'

import { createPublicApi } from '../api/public'
import { createCognitoAuthClient, type StaffPoolConfig } from './cognito'
import type { AuthClient } from './contract'
import { createFakeAuthClient } from './fake'

export const AUTH_CLIENT: InjectionKey<AuthClient> = Symbol('match-point.auth-client')

export interface AuthClientOptions {
  /**
   * The org's display name through the authenticated `ApiClient`, or `null`
   * when it cannot be had. A thunk because auth is constructed before the api
   * client — the api client's token getter needs auth — and the first call
   * that needs an org name happens after both exist. The fake ignores it.
   */
  getOrgName?: (orgId: string) => Promise<string | null>
}

/**
 * Cognito when it is configured, the fake otherwise.
 *
 * The condition is the presence of configuration rather than a separate mode
 * flag: those are the same question asked twice, and two ways to ask it is one
 * way to disagree. A build with a pool id is talking to a real pool.
 *
 * Half-present configuration throws rather than falling back. A silent fallback
 * would let a deployed build serve the fake — signing people in as seeded
 * accounts that do not exist, against an API that would refuse every request
 * they then made.
 */
export function createAuthClient (options: AuthClientOptions = {}): AuthClient {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL

  if (!userPoolId && !clientId) return createFakeAuthClient()

  if (!userPoolId || !clientId || !apiBaseUrl) {
    throw new Error(
      'Cognito is half-configured. VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_CLIENT_ID and ' +
        'VITE_API_BASE_URL are required together — signup goes through the API rather than ' +
        'Cognito, because the API is what creates the org and stamps the token claims.'
    )
  }

  const staff = staffConfig()

  return createCognitoAuthClient(
    {
      userPoolId,
      clientId,
      // Optional: without it federated sign-in is off and email and password
      // still work. ADR 0012's domain exists only once its certificate does.
      domain: import.meta.env.VITE_COGNITO_DOMAIN || undefined,
      staff: staff?.pool
    },
    {
      publicApi: createPublicApi(apiBaseUrl),
      getOrgName: options.getOrgName ?? (async () => null),
      staffApi: staff ? createPublicApi(staff.apiBaseUrl) : undefined
    }
  )
}

/**
 * The staff pool and the staff API (ADR 0016), or null when this build has none.
 *
 * All four or none, and half-present throws, for the reason the customer pool's
 * half-configuration does: a staff link that sends somebody to a pool whose API
 * this build cannot reach would sign them in and then refuse every request.
 */
export function staffConfig (env: Partial<ImportMetaEnv> = import.meta.env): { pool: StaffPoolConfig, apiBaseUrl: string } | null {
  const values = {
    userPoolId: env.VITE_STAFF_COGNITO_USER_POOL_ID,
    clientId: env.VITE_STAFF_COGNITO_CLIENT_ID,
    domain: env.VITE_STAFF_COGNITO_DOMAIN,
    apiBaseUrl: env.VITE_STAFF_API_BASE_URL
  }
  const present = Object.values(values).filter(Boolean).length

  if (present === 0) return null

  if (present < 4 || !values.userPoolId || !values.clientId || !values.domain || !values.apiBaseUrl) {
    throw new Error(
      'Staff sign-in is half-configured. VITE_STAFF_COGNITO_USER_POOL_ID, VITE_STAFF_COGNITO_CLIENT_ID, ' +
        'VITE_STAFF_COGNITO_DOMAIN and VITE_STAFF_API_BASE_URL are required together.'
    )
  }

  return {
    pool: { userPoolId: values.userPoolId, clientId: values.clientId, domain: values.domain },
    apiBaseUrl: values.apiBaseUrl
  }
}

export function provideAuthClient (app: App, client: AuthClient): void {
  app.provide(AUTH_CLIENT, client)
}

export function useAuthClient (): AuthClient {
  const client = inject(AUTH_CLIENT)
  if (!client) throw new Error('No AuthClient provided. Call provideAuthClient() in main.ts.')
  return client
}

export * from './contract'
export { DEV_ACCOUNTS } from './fake'
